<template>
  <div style="margin-top: 60px" class="home">
    <Navbar :cartIsVisible="false"></Navbar>

    <div id="success-section" class="success-section">
      <h1 class="section-title" :style="{ color: sectionTitleColorTheme }">
        {{ $t("payment.yourOrderIsInProgress") }}
        <br />
        <br />{{ $t("payment.itWillReachYou") }}
        <span class="tableQR-header"
          >{{ $t("payment.desk") }} {{ tableQRLabel(tableQR) }}</span
        >
      </h1>
    </div>
    <SendEmailModal
      ref="sendEmailModal"
      :isPaid="isPaid"
      :paymentType="paymentType"
      :tableQR="tableQR"
      :paymentMethod="paymentMethod"
    ></SendEmailModal>
  </div>
</template>

<script>
import { getPaymentById, updatePayment } from "@/collections/payments";
import { firestoreTimestamp } from "@/firebaseConfig";
import Navbar from "@/components/public/common/Navbar";
import firebaseMixin from "@/mixins/firebase/firebaseMixin";
import SendEmailModal from "@/components/public/modals/SendEmailModal";
import SuccessMixin from "@/mixins/success/SuccessMixin";
import RedsysMixin from "@/mixins/payment/RedsysMixin";
import { getQrZoneById } from "@/collections/qrZone";
import { saveOrder } from "@/collections/orders";
import { getQRByTable } from "@/collections/qrs";
import { deleteAllUserCartByTable } from "@/collections/userCart";
let merchantParams = null;

export default {
  name: "SuccessRedsys",
  mixins: [firebaseMixin, SuccessMixin, RedsysMixin],
  props: ["sessionId"],
  components: {
    Navbar,
    SendEmailModal
  },
  data() {
    return {
      total: 0,
      isPaid: false, // should pay or request
      paymentType: "product",
      paymentMethod: "redsys",
      canBeDirect: false,
      lastOrder: this.$store.state.cart
    };
  },
  computed: {
    homeUrl() {
      return `/?qr=${this.tableQR}`;
    },

    fromCheckout() {
      return window.localStorage.getItem("lastLand") === "checkout";
    },
    paymentId() {
      return this.$router.currentRoute.query.p_id;
    }
  },
  created() {
    // if the cart is empty and the user doesn't come from the checkout, redirect to home
    if (this.$store.state.cart.length === 0 && !this.fromCheckout) {
      this.$router.replace(this.homeUrl);
    }
  },
  async mounted() {
    try {
      // is any type of payment done or just requested
      if (this.paymentId) {
        // variable for if user want to pay
        this.paymentType = localStorage.getItem("payment_type") || "product"; // "product" or "amount"

        merchantParams = this.verifyPayment();
        if (!merchantParams) {
          this.$router.replace(this.homeUrl);
          return;
        }

        let payment = await getPaymentById(this.paymentId);

        // check if payment already marked as success
        if (payment && payment.success) {
          this.$router.replace(this.homeUrl);
          return;
        }

        this.canBeDirect = await this.isDirectPayment();

        // direct payment
        if (this.canBeDirect) {
          this.handleDirectPayment(); // direct payment
        } else {
          this.handleIndirectPayment();
        }
        this.successRedsysPayment();
      } else {
        // save order as requested
        await this.saveSuccessOrder(); // save orders only

        this.successRedsysPayment();
      }
      this.clearCart();
    } catch (error) {
      this.setlogError(
        "get_payment_by_Id",
        "Obtained error message in mounted()",
        error.stack,
        this.$options.name
      );
    }
  },
  methods: {
    async isDirectPayment() {
      try {
        let table = await getQRByTable(this.tableQR);
        if (table) {
          let zoneQR = await getQrZoneById(table.zone_id);
          return zoneQR ? zoneQR.acceptDirectPayment : false;
        }
        return false;
      } catch (error) {
        // eslint-disable-next-line
        this.setlogError(
          "get/qr_by_table/qr_zone_by_id",
          "Obtained error message in isDirectPayment()",
          error.stack,
          this.$options.name
        );
        return false;
      }
    },
    async handleDirectPayment() {
      try {
        let self = this;

        let time = firestoreTimestamp.fromDate(new Date());
        let orders = [...this.cart];
        this.cart.forEach(item => {
          let id = self.uuidv4();
          saveOrder(id, self.buildOrder(item, time, id));
          self.total += item.price * item.quantity;
        });

        // Update success: true to prevent using same URL to make other orders
        await updatePayment(this.paymentId, {
          success: true
        });
        this.$refs.sendEmailModal.openDialog(
          orders,
          self.total,
          this.canBeDirect
        );
      } catch (error) {
        this.setlogError(
          "update_payment",
          "Obtained error message in handleDirectPayment()",
          error.stack,
          this.$options.name
        );
      }
    },
    async handleIndirectPayment() {
      try {
        this.saveOrdersWithAdditionalCharge();

        // deduct amount from remaining to pay
        await this.updateOrderTotalByTable();

        if (this.paymentType === "product") {
          // set is paid to true in particular order which is selected in ProductChooserDialog
          this.setIsPaid();
        }

        // Update success: true to prevent using same URL to make other orders
        await updatePayment(this.paymentId, {
          success: true
        });
        this.$refs.sendEmailModal.openDialog(
          [...this.cart],
          self.total,
          this.canBeDirect
        );
      } catch (error) {
        this.setlogError(
          "update_payment",
          "Obtained error message in handleIndirectPayment()",
          error.stack,
          this.$options.name
        );
      }
    },
    async saveSuccessOrder() {
      let time = firestoreTimestamp.fromDate(new Date());
      try {
        this.saveOrdersWithAdditionalCharge();

        // save amount in orders_total_by_table collection
        await this.addAmountOrderTotalByTable(time);

        this.$router.push("/receipt");
      } catch (error) {
        this.setlogError(
          "create_order",
          "Obtained error message",
          error.stack,
          this.$options.name
        );
      }
    },
    async successRedsysPayment() {
      let time = firestoreTimestamp.fromDate(new Date());
      try {
        this.saveCard(merchantParams);

        // set total order quantity in localstorage
        this.saveMyOrders();
        this.saveGeneralComment(time);
        this.showSuccessMessage("Pedido Realizado");

        // create order user log
        this.setlogInfo(
          "create_order",
          "Pedido creado",
          [...this.cart],
          this.$options.name
        );
      } catch (error) {
        this.setlogError(
          "create_order",
          "Obtained error message",
          error.stack,
          this.$options.name
        );
        console.error(error);
      }
    },
    verifyPayment() {
      let merchantParameters = this.$router.currentRoute.query
        .Ds_MerchantParameters;

      if (merchantParameters) {
        merchantParameters = this.processNotificationParameters(
          merchantParameters
        );
        console.log(
          "PAYMENT SUCCESS",
          JSON.stringify(merchantParameters, null, 2)
        );
        if (merchantParameters.Ds_Response === "0000") {
          return merchantParameters;
        }
        return false;
      }
      return false;
    },
    async clearCart() {
      // clear everything from local storage
      await deleteAllUserCartByTable(this.tableQR, false);
      this.$store.commit("removeAllCart");
      window.localStorage.setItem("lastLand", "");
    },
    saveCard(merchantParameters) {
      if (!merchantParameters) return;
      let currentCard = {
        cardNo: merchantParameters.Ds_CardNumber,
        identifier: merchantParameters.Ds_Merchant_Identifier
      };

      let foundSameCard = this.$store.state.redsysCards.some(item => {
        if (item.cardNo === merchantParameters.Ds_CardNumber) {
          return item;
        }
      });

      if (foundSameCard) {
        this.$store.state.redsysCards = this.$store.state.redsysCards.map(
          item => {
            if (item.cardNo === currentCard.cardNo) {
              item.cardNo = currentCard.cardNo;
              item.identifier = currentCard.identifier;
            }
            return item;
          }
        );
        this.$store.commit("saveRedsysCardData");
        return;
      }

      this.$store.commit("addRedsysCards", currentCard);
    },
    saveOrdersWithAdditionalCharge() {
      let time = firestoreTimestamp.fromDate(new Date());
      this.cart.forEach(item => {
        let id = self.uuidv4();
        saveOrder(id, self.buildOrder(item, time, id));
        self.total += item.isMenuGroupProduct
          ? item.price * item.quantity + this.totalWithAdditionalCharge(item)
          : item.price * item.quantity;
      });
    }
  }
};
</script>

<style scoped lang="scss"></style>
